import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import LocationHistoryModal from './LocationHistoryModal';
import { parseESCVersion, getESCStatusClasses, formatFirmwareVersion } from '../../utils/esc-utils';
import { formatLastSeenWithTime, getFullDateTime } from '../../utils/date-utils';
import { buttonClass } from '../../components/ui/Button';
import { Panel } from '../../components/ui/Panel';
import FadeIn from '../../components/ui/FadeIn';
import Reveal from '../../components/ui/Reveal';
import { CATEGORY, alpha } from '../../components/ui/categoryColors';

function DroneInfo({ drone, serialReadable, serialNumber, isDesktop, showQRKey }) {
    const [locationName, setLocationName] = useState(null);
    const [showHistoryModal, setShowHistoryModal] = useState(false);

    const formatToMMDDYY = dateString => {
        if (!dateString) return '';
        return dayjs(dateString).format('MM - DD - YY');
    };

    // Reverse geocode GPS coordinates to get city name
    useEffect(() => {
        if (!drone.last_seen_location) {
            setLocationName(null);
            return;
        }

        const [lat, lon] = drone.last_seen_location.split(',');
        if (!lat || !lon) {
            setLocationName(null);
            return;
        }

        // Use OpenStreetMap Nominatim for reverse geocoding
        const fetchLocationName = async () => {
            try {
                const response = await fetch(
                    `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}&zoom=10`,
                    {
                        headers: {
                            'User-Agent': 'FirefleetDroneManagement/1.0'
                        }
                    }
                );
                const data = await response.json();

                // Extract city, town, or village
                const city = data.address?.city ||
                            data.address?.town ||
                            data.address?.village ||
                            data.address?.county;

                // Extract state/province
                const state = data.address?.state || data.address?.province;

                // Combine city and state
                let location = 'Unknown Location';
                if (city && state) {
                    location = `${city}, ${state}`;
                } else if (city) {
                    location = city;
                } else if (state) {
                    location = state;
                }

                setLocationName(location);
            } catch (error) {
                console.error('Failed to geocode location:', error);
                setLocationName(null);
            }
        };

        fetchLocationName();
    }, [drone.last_seen_location]);

    const getActivateDate = () => {
        if (drone.notes && drone.notes.startsWith('esp active')) {
            const activeString = drone.notes;
            const numericPart = activeString.match(/\d{6}/);
            if (numericPart) {
                const day = numericPart[0].slice(4);
                const month = numericPart[0].slice(2, 4);
                const year = numericPart[0].slice(0, 2);
                return `${month} - ${day} - 20${year}`;
            } else {
                return "May/June 2023";
            }
        } else {
            return "Not Activated";
        }
    };

    return (
        <>
            <Panel label="Drone details" className="mb-3">
                <ul className="m-0 list-none space-y-3 p-0 text-white">
                    {drone.last_seen_time && (
                        <li>
                            <div className='flex justify-between items-center mb-1'>
                                <div className='text-on-surface-variant text-sm'>LAST SEEN</div>
                                {drone.last_seen_location && (
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => setShowHistoryModal(true)}
                                            className={buttonClass('quiet', 'sm')}
                                        >
                                            History
                                        </button>
                                        <a
                                            href={`https://www.google.com/maps?q=${drone.last_seen_location}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className={buttonClass('filled', 'sm')}
                                        >
                                            Map
                                        </a>
                                    </div>
                                )}
                            </div>
                            <div className='flex flex-col gap-1'>
                                {/* Row 1: Time */}
                                <div
                                    title={getFullDateTime(drone.last_seen_time)}
                                >
                                    {formatLastSeenWithTime(drone.last_seen_time)}
                                </div>
                                {/* Row 2: Location */}
                                {drone.last_seen_location && (
                                    <FadeIn key={locationName ? 'place' : 'coords'} className='text-sm text-on-surface-variant'>
                                        {locationName || drone.last_seen_location}
                                    </FadeIn>
                                )}
                                {/* Row 3: GCS kit + customer */}
                                {drone.last_seen_server && (
                                    <div
                                        className='text-xs text-on-surface-variant'
                                        title={drone.last_seen_server}
                                    >
                                        {drone.last_seen_gcs_code
                                            ? `${drone.last_seen_gcs_code} - ${drone.last_seen_customer_name || "Firefly"}`
                                            : drone.last_seen_server}
                                    </div>
                                )}
                            </div>
                        </li>
                    )}
                    <li>
                        <div className='text-on-surface-variant text-sm mb-1'>FAA REGISTRATION</div>
                        <div className='flex justify-between items-center'>
                            <div className="font-mono">{drone.faa_reg ? drone.faa_reg : "unregistered"}</div>
                            <div className='text-on-surface-variant text-sm'>
                                exp: {drone.reg_exp && `${formatToMMDDYY(drone.reg_exp)}`}
                            </div>
                        </div>
                    </li>
                    {drone.project_assignment && (
                        <li>
                            <div className='text-on-surface-variant text-sm mb-1'>PROJECT ASSIGNMENT</div>
                            <div>{drone.project_assignment}</div>
                        </li>
                    )}
                    <li>
                        <div className='text-on-surface-variant text-sm mb-1'>CUSTOMER</div>
                        <div className='flex flex-wrap items-baseline gap-x-2'>
                            <span>
                                {drone.customer_org_name || <span className='italic' style={{ color: alpha(CATEGORY.amber.ink, 0.8) }}>Firefly Internal</span>}
                            </span>
                            {drone.customer_org_id && (
                                <span className='text-xs' style={{ color: (drone.is_internal_pool ? CATEGORY.blue : CATEGORY.green).ink.oklch }}>
                                    ({drone.is_internal_pool ? 'Lease' : 'Sale'})
                                </span>
                            )}
                        </div>
                    </li>
                    <li>
                        <div className='text-on-surface-variant text-sm mb-1'>ACTIVATED</div>
                        <div>{getActivateDate()}</div>
                    </li>
                    {/* !mt-0: the list's space-y gap sits inside the Reveal, so it grows and leaves with it. */}
                    <li className='!mt-0'>
                        <Reveal show={!!showQRKey}>
                            <div className='pt-3'>
                                <div className='text-on-surface-variant text-sm mb-1'>QR KEY</div>
                                <div className="font-mono">{serialReadable}</div>
                            </div>
                        </Reveal>
                    </li>
                </ul>
            </Panel>

            {/* Telemetry Section - Firmware/ESP/ESC */}
            {(drone.firmware_version || drone.esp_fw_git_sha || drone.esc_version_result !== null) && (
                <Panel title="Telemetry" titleStyle="section" className="mb-3">
                    <ul className="m-0 list-none space-y-3 p-0 text-white">
                        {drone.firmware_version && (
                            <li>
                                <div className='text-on-surface-variant text-sm mb-1'>FIRMWARE</div>
                                <div
                                    className="font-mono"
                                    title={formatFirmwareVersion(drone.firmware_version, drone.firmware_label).title}
                                >
                                    {formatFirmwareVersion(drone.firmware_version, drone.firmware_label).display}
                                </div>
                            </li>
                        )}
                        {drone.esp_fw_git_sha && (
                            <li>
                                <div className='text-on-surface-variant text-sm mb-1'>ESP FIRMWARE</div>
                                <div
                                    className="font-mono"
                                    title={drone.esp_fw_git_sha}
                                >
                                    {drone.esp_fw_git_sha.substring(0, 7)}
                                </div>
                            </li>
                        )}
                        {drone.esc_version_result !== null && drone.esc_version_result !== undefined && (
                            <li>
                                <div className='text-on-surface-variant text-sm mb-1'>ESC VERSION</div>
                                {(() => {
                                    const esc = parseESCVersion(
                                        drone.esc_version_result,
                                        drone.esc_version_type,
                                        drone.esc_version_major,
                                        drone.esc_version_minor
                                    );
                                    return (
                                        <div className={`font-mono ${getESCStatusClasses(esc.display, esc.status)}`}>
                                            {esc.display}
                                        </div>
                                    );
                                })()}
                            </li>
                        )}
                    </ul>
                </Panel>
            )}

            <LocationHistoryModal
                isOpen={showHistoryModal}
                onClose={() => setShowHistoryModal(false)}
                serialNumber={serialNumber}
                isDesktop={isDesktop}
            />
        </>
    );
}

export default DroneInfo;
